<template>
  <!-- Keep teleport destinations mounted when navigating through pages without breadcrumbs. -->
  <nav v-show="crumbs.length > 1 || !!pageNavigation.current" class="app-breadcrumbs" :aria-label="t('navigation.breadcrumbs')">
    <v-btn v-if="pageNavigation.current && phone" class="page-sidebar-toggle" :icon="true" variant="flat" color="primary" :aria-label="t('navigation.openSidebar')" :aria-expanded="pageNavigation.current.expanded()" aria-haspopup="dialog" @click="togglePageSidebar">
      <svg class="page-sidebar-toggle__icon" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
    </v-btn>
    <EventActionMenu v-if="phone && route.params.clubId && route.params.seasonId && route.params.tryoutId && !route.path.includes('/setup')" :club-id="String(route.params.clubId)" :season-id="String(route.params.seasonId)" :tryout-id="String(route.params.tryoutId)" />
    <div id="page-navigation-menu" class="app-breadcrumbs__menu" />
    <ol ref="trail">
      <li v-for="(crumb, index) in crumbs" :key="`${crumb.key}-${index}`" :class="{'app-breadcrumbs__club':crumb.key === 'club','app-breadcrumbs__season':crumb.key === 'season','app-breadcrumbs__event':crumb.key === 'event'}">
        <v-icon v-if="index" icon="mdi-chevron-right" size="15" aria-hidden="true" />
        <SeasonEventSwitcher v-if="crumb.key === 'event'" :club-id="String(route.params.clubId)" :season-id="String(route.params.seasonId)" :current-event-id="String(route.params.tryoutId)" show-when-single>
          <template #activator="{ props: menuProps, open }">
            <BreadcrumbMenuTrigger icon="mdi-clipboard-check-outline" :label="label(crumb)" :to="crumb.to" :menu-props="menuProps" :menu-label="t('shell.eventSwitcher.change')" @open="open" />
          </template>
        </SeasonEventSwitcher>
        <span v-else-if="index === crumbs.length - 1 && crumb.key !== 'event'" aria-current="page" :title="label(crumb)"><v-icon v-if="crumb.key === 'club' || crumb.key === 'season'" :icon="crumb.key === 'club' ? 'mdi-domain' : 'mdi-calendar-range'" size="16" aria-hidden="true" />{{ label(crumb) }}</span>
        <router-link v-else :aria-current="index === crumbs.length - 1 ? 'page' : undefined" :to="crumb.to" :title="label(crumb)"><v-icon v-if="crumb.key === 'club' || crumb.key === 'season'" :icon="crumb.key === 'club' ? 'mdi-domain' : 'mdi-calendar-range'" size="16" aria-hidden="true" />{{ label(crumb) }}</router-link>
      </li>
      <li id="page-navigation-context" class="app-breadcrumbs__context" />
    </ol>
  </nav>
</template>

<script setup lang="ts">
import {useCurrentEventMetadata} from './useCurrentEventMetadata'
import EventActionMenu from '@/components/EventActionMenu.vue'
import { pluralizeModelNoun } from '@/utils/modelNouns'
import BreadcrumbMenuTrigger from './BreadcrumbMenuTrigger.vue'
import SeasonEventSwitcher from '@/components/SeasonEventSwitcher.vue'
import {usePageNavigationStore} from '@/stores/pageNavigation'
import {useCompactPageNavigation} from '@/composables/useCompactLayout'
import { useResizeObserver } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useClubStore } from '@/stores/club'
import { breadcrumbsFor, type Breadcrumb } from './navigation'

const pageNavigation=usePageNavigationStore(),phone=useCompactPageNavigation()
const route = useRoute(), router = useRouter(), { t, te } = useI18n(), clubs = useClubStore()
const {currentEvents,cacheRevision}=useCurrentEventMetadata()
const queryClient=useQueryClient(),auth=useAuthStore()
function togglePageSidebar(event:MouseEvent){pageNavigation.current?.toggle(event.currentTarget instanceof HTMLElement?event.currentTarget:undefined)}
const participantLabel = computed(() => {
  const event = currentEvents.value.find(event => event?.participantNoun)
  const noun = event?.participantNoun
  if (!noun) return t('navigation.participants')
  const plural = pluralizeModelNoun(noun)
  return plural.charAt(0).toUpperCase() + plural.slice(1)
})
const sessionLabel = computed(() => { const noun=currentEvents.value.find(event=>event?.sessionNoun)?.sessionNoun || t('tryoutSetup.sessions.sessionFallback'); return noun.charAt(0).toUpperCase()+noun.slice(1) })
const crumbs = computed(() => breadcrumbsFor(route, router.options.history.state.settingsReturnTo))
const trail = ref<HTMLOListElement | null>(null)
useResizeObserver(trail, () => { if (trail.value) trail.value.scrollLeft = trail.value.scrollWidth })
watch(() => route.fullPath, async () => {
  await nextTick()
  if (trail.value) trail.value.scrollLeft = trail.value.scrollWidth
}, { immediate: true })
function label(crumb: Breadcrumb) {
  if (crumb.key === 'sessionPlan') {
    void cacheRevision.value
    const data = queryClient.getQueryData<{ session?: { title?: string } }>(['sessionPlan', String(route.params.clubId), String(route.params.seasonId), String(route.params.tryoutId), String(route.params.sessionId), auth.user?.id || ''])
    return data?.session?.title || t('navigation.sessionPlan')
  }
  if (crumb.key === 'event') return t('navigation.thisEvent')
  if (crumb.key === 'season') return t('navigation.thisSeason')
  if (crumb.key === 'club') return clubs.data[String(route.params.clubId)]?.name || t('navigation.club')
  if (crumb.key === 'participants') return participantLabel.value
  if (crumb.key === 'importParticipants') return t('navigation.importParticipants', { participant: participantLabel.value })
  if (crumb.tab) return te(crumb.tab) ? t(crumb.tab, { participant: participantLabel.value, session: sessionLabel.value }) : t('navigation.section')
  return t(`navigation.${crumb.key}`)
}
</script>

<style scoped>
.app-breadcrumbs { display: flex; align-items: center; gap: 12px; padding: 6px 18px; min-height: 48px; background: linear-gradient(110deg, var(--cs-soft-white), #faf7ff 75%, #fff4eb); border-bottom: 1px solid var(--cs-border); color: var(--cs-muted); font-size: .8rem; }
.app-breadcrumbs__context:empty, .app-breadcrumbs__menu:empty, .app-breadcrumbs__switcher:empty { display: none; }
.app-breadcrumbs .page-sidebar-toggle{color:white!important;background:var(--cs-action)!important;flex:0 0 auto;min-width:44px!important;width:44px!important;height:44px!important;border-radius:8px!important}
.app-breadcrumbs__menu { flex: 0 0 auto; }
.app-breadcrumbs__switcher { margin-left: auto; min-width: 0; max-width: 35%; }
.app-breadcrumbs ol { display: flex; align-items: center; gap: 6px; list-style: none; margin: 0; padding: 3px; min-width: 0; overflow-x: auto; flex: 1; white-space: nowrap; }
.app-breadcrumbs li { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; }
.app-breadcrumbs a { color: inherit; text-decoration: none; padding-block: 8px; }
.app-breadcrumbs a:hover { color: var(--cs-accent); text-decoration: underline; }
.app-breadcrumbs li:last-child { color: var(--cs-text); font-weight: 600; }
.app-breadcrumbs a:focus-visible { outline: 2px solid var(--cs-electric); outline-offset: 2px; border-radius: 3px; }
.app-breadcrumbs :deep(.v-btn) { color: var(--cs-accent) !important; height: 36px; text-transform: none; letter-spacing: 0; }
@media(max-width:600px) {
 .app-breadcrumbs { gap: 8px; padding: 4px 12px; }
 .app-breadcrumbs ol { flex-direction: row; }
 .app-breadcrumbs__switcher { max-width: 40px; }
 .app-breadcrumbs__switcher :deep(.season-event-switcher) { min-width: 36px; padding: 0 8px; }
 .app-breadcrumbs__switcher :deep(.v-btn__content), .app-breadcrumbs__switcher :deep(.v-btn__append) { display: none; }
 .app-breadcrumbs__switcher :deep(.v-btn__prepend) { margin: 0; }
}
</style>

<style scoped>
.app-breadcrumbs ol { gap: 8px; }
.app-breadcrumbs li { gap: 8px; }
.app-breadcrumbs li > .v-icon { color: #a78abc; }
.app-breadcrumbs li > [aria-current='page'] { background: var(--cs-selection); color: var(--cs-accent); padding: 6px 10px; border-radius: 7px; font-weight: 650; }
.app-breadcrumbs a { border-radius: 6px; padding-inline: 3px; }
.app-breadcrumbs a:hover { background: #eee0ff; text-decoration: none; }
.app-breadcrumbs__menu :deep(.v-btn) { background: var(--cs-action); color: white !important; border-radius: 8px; width: 36px; }
.app-breadcrumbs__switcher :deep(.v-btn) { background: white; border: 1px solid var(--cs-border); border-radius: 8px; }
</style>

<style scoped>
.app-breadcrumbs__context:empty { display: none; }
.app-breadcrumbs__context { gap: 8px; }
</style>

<style scoped>
@media(max-width:900px), (max-width:950px) and (max-height:520px) and (orientation:landscape){
 .app-breadcrumbs{position:sticky;top:0;z-index:1003;box-shadow:0 2px 8px rgb(32 20 56 / 8%)}
}
</style>

<style scoped>
/* Persistent destination identities, including their current-page states. */
.app-breadcrumbs .app-breadcrumbs__club > a,
.app-breadcrumbs .app-breadcrumbs__club > span,
.app-breadcrumbs .app-breadcrumbs__season > a,
.app-breadcrumbs .app-breadcrumbs__season > span {
  display:inline-flex;align-items:center;gap:6px;min-height:34px;padding:6px 10px;
  border:1px solid var(--cs-border);border-radius:8px;font-weight:650;
}
.app-breadcrumbs .app-breadcrumbs__club > a,
.app-breadcrumbs .app-breadcrumbs__club > span {
  background:var(--cs-indigo);border-color:var(--cs-indigo);color:white;
}
.app-breadcrumbs .app-breadcrumbs__club > a:hover {background:var(--cs-violet);border-color:var(--cs-violet);color:white;}
.app-breadcrumbs .app-breadcrumbs__season > a,
.app-breadcrumbs .app-breadcrumbs__season > span {
  background:var(--cs-soft-white);border-color:var(--cs-action);color:var(--cs-action);
}
.app-breadcrumbs .app-breadcrumbs__season > a:hover {background:var(--cs-selection);}
.app-breadcrumbs .app-breadcrumbs__club > [aria-current='page'],
.app-breadcrumbs .app-breadcrumbs__season > [aria-current='page'] {box-shadow:0 0 0 2px var(--cs-sunset);}
</style>

<style scoped>
.app-breadcrumbs .app-breadcrumbs__event > .breadcrumb-choice {
  background:var(--cs-soft-white);border:1px solid var(--cs-action);color:var(--cs-action);
  border-radius:8px;font-weight:650;min-height:34px;
}
.app-breadcrumbs .app-breadcrumbs__event :deep(.breadcrumb-choice__label) { padding:6px 10px; }
@media(min-width:601px) {
  .app-breadcrumbs .app-breadcrumbs__event :deep(.breadcrumb-choice__label),
  .app-breadcrumbs .app-breadcrumbs__event :deep(.breadcrumb-choice__arrow) { min-height:32px; }
}
</style>
